/** Focus helpers for the drawer and the edit panel: keep Tab inside, give focus back on close. */

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getClientRects().length > 0,
  );
}

/**
 * While `active`: Tab and Shift+Tab cycle inside `ref`, Escape calls `onEscape`, and focus moves
 * into the container (the `initial` element, else a [data-autofocus] one, else the container itself,
 * so a phone keyboard does not pop up over the form). On release
 * focus returns to whatever had it before.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement>,
  active: boolean,
  onEscape: () => void,
  initial?: RefObject<HTMLElement>,
): void {
  const escape = useRef(onEscape);
  escape.current = onEscape;
  useEffect(() => {
    if (!active) return undefined;
    const root = ref.current;
    if (!root) return undefined;
    const had = document.activeElement as HTMLElement | null;
    // A field that took focus itself (autoFocus) keeps it.
    const inside = !!had && root.contains(had);
    const before = inside ? null : had;
    if (!inside) {
      const first = initial?.current ?? root.querySelector<HTMLElement>('[data-autofocus]') ?? null;
      (first ?? root).focus({ preventScroll: true });
    }

    const onKey = (e: KeyboardEvent): void => {
      if (e.defaultPrevented) return; // a panel opened inside this one handled it
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        escape.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableIn(root);
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const firstEl = items[0]!;
      const lastEl = items[items.length - 1]!;
      const current = document.activeElement;
      if (e.shiftKey && (current === firstEl || current === root)) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && current === lastEl) {
        e.preventDefault();
        firstEl.focus();
      } else if (current && !root.contains(current)) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    root.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('keydown', onKey);
      if (before && typeof before.focus === 'function' && document.contains(before)) before.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
